import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { blogPosts } from '@/data/blogPosts';
import { ArrowLeft, ArrowRight, Clock, Tag, Phone } from 'lucide-react';

function ContentBlock({ block }) {
  if (block.type === 'heading') {
    return (
      <h2 className="font-heading font-bold text-xl tracking-tight text-brand-navy mt-10 mb-4">
        {block.text}
      </h2>
    );
  }
  if (block.type === 'paragraph') {
    return (
      <p className="text-base text-[#222222] leading-relaxed mb-4">
        {block.text}
      </p>
    );
  }
  if (block.type === 'list') {
    return (
      <ul className="space-y-2 mb-6 ml-1">
        {block.items.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-base text-[#222222] leading-relaxed">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-green mt-2.5 flex-shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.id === slug);

  useSEO({
    title: post ? post.title : 'Blog Post Not Found',
    description: post ? post.description : '',
    image: post?.image,
  });

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const currentIndex = blogPosts.findIndex((p) => p.id === slug);
  const nextPost = blogPosts[currentIndex + 1] || null;
  const prevPost = blogPosts[currentIndex - 1] || null;

  return (
    <div data-testid="blog-post-page">
      {/* Hero */}
      <section className="bg-brand-navy py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-6">
          <AnimatedSection>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-green">
                <Tag className="w-3 h-3" /> {post.category}
              </span>
              <span className="text-xs text-slate-400">{post.date}</span>
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Clock className="w-3 h-3" /> {post.readTime}
              </span>
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-white">
              {post.title}
            </h1>
          </AnimatedSection>
        </div>
      </section>

      {/* Featured Image */}
      <div className="max-w-3xl mx-auto px-6 -mt-2">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-64 sm:h-80 object-cover rounded-lg"
          loading="lazy"
        />
      </div>

      {/* Content */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <AnimatedSection>
            <article data-testid="blog-post-content">
              {post.content.map((block, i) => (
                <ContentBlock key={i} block={block} />
              ))}
            </article>
          </AnimatedSection>

          {/* CTA Banner */}
          <AnimatedSection delay={0.1}>
            <div className="mt-14 bg-brand-muted border border-slate-200 rounded-lg p-8">
              <h3 className="font-heading font-semibold text-lg text-brand-navy mb-3">
                Need personalized help with your taxes?
              </h3>
              <p className="text-sm text-[#222222] leading-relaxed mb-5">
                Every tax situation is different. If this article raised questions about your own taxes, we can help.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/ask">
                  <Button
                    data-testid="blogpost-cta-ask"
                    className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold rounded-md"
                  >
                    Ask a Tax Question - Free <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href="tel:8555892461">
                  <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold rounded-md">
                    <Phone className="w-4 h-4 mr-2" /> 855-589-2461
                  </Button>
                </a>
              </div>
            </div>
          </AnimatedSection>

          {/* Prev / Next */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {prevPost && (
              <Link
                to={`/blog/${prevPost.id}`}
                data-testid="blog-nav-prev"
                className="p-5 border border-slate-200 rounded-lg hover:border-brand-green/40 transition-colors group"
              >
                <p className="text-xs text-slate-400 mb-1">Previous</p>
                <p className="text-sm font-semibold text-brand-navy group-hover:text-brand-green transition-colors">
                  {prevPost.title}
                </p>
              </Link>
            )}
            {nextPost && (
              <Link
                to={`/blog/${nextPost.id}`}
                data-testid="blog-nav-next"
                className={`p-5 border border-slate-200 rounded-lg hover:border-brand-green/40 transition-colors group text-right ${!prevPost ? 'sm:col-start-2' : ''}`}
              >
                <p className="text-xs text-slate-400 mb-1">Next</p>
                <p className="text-sm font-semibold text-brand-navy group-hover:text-brand-green transition-colors">
                  {nextPost.title}
                </p>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
